import React, { useEffect, useState } from 'react';
import {
    DefaultButton,
    Panel,
    PanelType,
    PrimaryButton,
    TextField,
    Checkbox,
    IPanelStyles,
    Dropdown,
    IDropdownOption,
    mergeStyleSets,
} from '@fluentui/react';
import DateTimePickerComponent from '../date-time-picker/date-time-picker.component';
import CustomerCardComponent, { ICustomerCardContactFields } from '../customer-card/customer-card.component';
import LoaderComponent from '../loader/loader.component';
import ErrorMessageComponent from '../error-message/error-message.component';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemOrdenDeTrabajo from '../../models/ItemOrdenDeTrabajo';
import ItemTarea from '../../models/ItemTarea';
import ItemDireccion from '../../models/ItemDireccion';
import ItemCliente from '../../models/ItemCliente';
import ItemOperario from '../../models/ItemOperario';
import ItemTipoIncidencia from '../../models/ItemTipoIncidencia';

const panelStyles: IPanelStyles = {
    root: {}, overlay: {}, hiddenPanel: {}, main: {}, commands: { marginTop: 0 },
    contentInner: {}, scrollableContent: {},
    navigation: { backgroundColor: 'var(--color-primario)', padding: 10, alignItems: 'center' },
    header: { paddingLeft: 10, paddingRight: 10, paddingTop: 5, paddingBottom: 5 },
    headerText: { color: '#ffffff' },
    content: { padding: 0 },
    footer: {}, footerInner: {},
    subComponentStyles: {
        closeButton: {
            root: { color: '#ffffff', backgroundColor: 'transparent', padding: 0 },
            rootHovered: { backgroundColor: '#000000', color: '#ffffff', borderColor: '#000000' },
        },
    },
};

const styles = mergeStyleSets({
    sectionHeader: {
        display: 'block',
        backgroundColor: 'var(--color-primario)',
        color: 'white',
        padding: '4px 12px',
        fontWeight: 600,
        fontSize: '13px',
        marginBottom: '4px',
        marginTop: '8px',
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'flex-start',
        padding: '4px 12px',
        gap: 8,
    },
    fieldLabel: {
        minWidth: 180,
        fontSize: 13,
        color: '#444',
        fontWeight: 500,
        paddingTop: 6,
        flexShrink: 0,
    },
    fieldLabelOptional: {
        fontSize: 11,
        color: '#999',
        fontWeight: 400,
        marginLeft: 4,
    },
    fieldValue: {
        flex: 1,
        minWidth: 0,
    },
    twoCol: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0 8px',
        padding: '0 12px',
    },
    checkRow: {
        padding: '4px 12px',
    },
    footerQuick: {
        display: 'flex',
        gap: 8,
        padding: '12px',
        borderTop: '1px solid #ececec',
        justifyContent: 'flex-end',
        backgroundColor: '#f8f8f8',
    },
    footerFull: {
        display: 'flex',
        gap: 8,
        padding: '12px',
        borderTop: '1px solid #ececec',
        justifyContent: 'space-between',
        backgroundColor: '#f8f8f8',
    },
    buttonGroup: {
        display: 'flex',
        gap: 8,
    },
    fieldRowFirst: {
        display: 'flex',
        alignItems: 'flex-start',
        padding: '12px 12px 4px 12px',
        gap: 8,
    },
    saveButton: {
        backgroundColor: 'var(--color-primario)',
        borderColor: 'var(--color-primario)',
    },
});

interface IWorkOrderCreateComponentProps {
    isOpen: boolean;
    onClose: () => void;
    onWorkOrderCreated: () => void;
    onWorkOrderCreatedAndEdit?: (id: number) => void;
}

const nowISO = () => new Date().toISOString();
const addMinutes = (iso: string, minutes: number) =>
    new Date(new Date(iso).getTime() + minutes * 60000).toISOString();

const WorkOrderCreateComponent = (props: IWorkOrderCreateComponentProps) => {
    const auth = useAuthStatus();

    const [workOrder, setWorkOrder] = useState<ItemOrdenDeTrabajo>(new ItemOrdenDeTrabajo());
    const [tarea, setTarea] = useState<ItemTarea>(new ItemTarea());
    const [tiempoPrevisto, setTiempoPrevisto] = useState<number>(0);
    const [delayShipment, setDelayShipment] = useState<boolean>(false);
    const [showMore, setShowMore] = useState<boolean>(false);
    const [addresses, setAddresses] = useState<ItemDireccion[] | undefined>(undefined);
    const [contactDisplayName, setContactDisplayName] = useState<string>('');
    const [operators, setOperators] = useState<ItemOperario[]>([]);
    const [incidentTypes, setIncidentTypes] = useState<ItemTipoIncidencia[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (auth.user) {
            const now = nowISO();
            setWorkOrder(wo => ({ ...wo, De: auth.user!.IdUsuario, FechaAlta: now }));
            setTarea(t => ({
                ...t,
                De: auth.user!.IdUsuario,
                FechaHoraInicioPrevista: addMinutes(now, 10),
                FechaPrevistaFin: addMinutes(now, 10),
                ControlDeTiempos: 1,
                Orden: 1,
            }));
        }
    }, [auth.user]);

    useEffect(() => {
        if (auth.user) {
            setLoading(true);
            ApiService.Post<ItemTipoIncidencia[]>('OrdenesTrabajo', 'GetListaTipoIncidencia', auth.user)
                .then((res: ItemTipoIncidencia[]) => {
                    setIncidentTypes(res);
                    if (auth.user!.UsuarioAdmin) {
                        ApiService.Post<ItemOperario[]>('OrdenesTrabajo', 'GetListaOperarios', auth.user!)
                            .then((res2: ItemOperario[]) => { setOperators(res2); setLoading(false); },
                                (err: string) => { setError(err); setLoading(false); });
                    } else {
                        const op = new ItemOperario();
                        op.IdUsuario = auth.user!.IdUsuario;
                        op.Nombre = auth.user!.Nombre;
                        setOperators([op]);
                        setLoading(false);
                    }
                }, (err: string) => { setError(err); setLoading(false); });
        }
        return () => {
            setWorkOrder(new ItemOrdenDeTrabajo());
            setTarea(new ItemTarea());
            setTiempoPrevisto(0);
            setDelayShipment(false);
            setShowMore(false);
            setAddresses(undefined);
            setContactDisplayName('');
            setError('');
        };
    }, [auth.user]);

    const onSelectCustomer = (item: ItemCliente) => {
        setContactDisplayName(item.Nombre || item.CodigoCliente);
        if (item.CodigoCliente === workOrder.CodigoCliente) return;
        setLoading(true);
        setAddresses(undefined);
        setWorkOrder(wo => ({
            ...wo,
            CodigoCliente: item.CodigoCliente,
            CodDireccion: 0,
            Nombre: item.Nombre || '',
            Direccion: '',
            CPostal: '',
            Provincia: '',
            Poblacion: '',
            Pais: '',
        }));
        if (auth.user) {
            ApiService.Post<ItemDireccion[]>('OrdenesTrabajo', 'GetCliente', auth.user, { CodCliente: item.CodigoCliente })
                .then((res: ItemDireccion[]) => {
                    setAddresses(res);
                    // Auto-fill with first address
                    if (res.length > 0) {
                        const first = res[0];
                        const codCliente = parseInt(first.CodCliente) || 0;
                        const codDireccion = parseInt(first.CodigoDireccion) || 0;
                        setWorkOrder(wo => ({
                            ...wo,
                            CodCliente: codCliente,
                            CodDireccion: codDireccion,
                            Nombre: first.Nombre || wo.Nombre,
                            Direccion: first.Direccion || '',
                            CPostal: first.CPostal || '',
                            Provincia: first.Provincia || '',
                            Poblacion: first.Poblacion || '',
                            Pais: first.Pais || '',
                        }));
                        setTarea(t => ({
                            ...t,
                            CodCliente: codCliente,
                            CodDireccion: codDireccion,
                            Nombre: first.Nombre || t.Nombre,
                            Direccion: first.Direccion || '',
                            CPostal: first.CPostal || '',
                            Provincia: first.Provincia || '',
                            Poblacion: first.Poblacion || '',
                            Pais: first.Pais || '',
                        }));
                    }
                    setLoading(false);
                }, (err: string) => { setAddresses([]); setError(err); setLoading(false); });
        }
    };

    const onAddressChanged = (fields: ICustomerCardContactFields) => {
        const match = (addresses || []).find(a => a.Nombre === fields.Nombre && a.Direccion === fields.Direccion)
            || (addresses || []).find(a => a.Nombre === fields.Nombre);
        const codCliente = parseInt(match?.CodCliente || '0') || 0;
        const codDireccion = parseInt(match?.CodigoDireccion || '0') || 0;
        setWorkOrder(wo => ({
            ...wo,
            CodCliente: codCliente || wo.CodCliente,
            CodDireccion: codDireccion,
            Nombre: fields.Nombre,
            Direccion: fields.Direccion,
            CPostal: fields.CPostal,
            Provincia: fields.Provincia,
            Poblacion: fields.Poblacion,
            Pais: fields.Pais,
        }));
        setTarea(t => ({
            ...t,
            CodCliente: codCliente || t.CodCliente,
            CodDireccion: codDireccion,
            Nombre: fields.Nombre,
            Direccion: fields.Direccion,
            CPostal: fields.CPostal,
            Provincia: fields.Provincia,
            Poblacion: fields.Poblacion,
            Pais: fields.Pais,
        }));
    };

    const onContactChanged = (fields: ICustomerCardContactFields) => {
        setWorkOrder(wo => ({
            ...wo,
            Nombre: fields.Nombre,
            Direccion: fields.Direccion,
            CPostal: fields.CPostal,
            Provincia: fields.Provincia,
            Poblacion: fields.Poblacion,
            Pais: fields.Pais,
        }));
    };

    const onChangeOperator = (_e: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<ItemOperario>) => {
        const id = option?.data?.IdUsuario || '';
        setWorkOrder(wo => ({ ...wo, Para: id }));
        setTarea(t => ({ ...t, Para: id }));
    };

    const onChangeIncidentType = (_e: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<ItemTipoIncidencia>) => {
        setWorkOrder(wo => ({
            ...wo,
            Serie: option?.data?.Serie || '',
            Division: option?.data?.Descripcion || '',
        }));
    };

    const onChangeTiempoPrevisto = (_e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, v?: string) => {
        const mins = parseInt(v || '0') || 0;
        setTiempoPrevisto(mins);
        setTarea(t => ({
            ...t,
            FechaPrevistaFin: addMinutes(t.FechaHoraInicioPrevista || nowISO(), mins),
            TiempoPrevisto: mins / 60,
        }));
    };

    const buildPayload = () => {
        const wo = {
            ...workOrder,
            Observaciones: 'Orden',
            Usuario: auth.user!.IdUsuario,
            CodigoCliente: auth.user!.CodigoCliente,
            De: auth.user!.IdUsuario,
            ListaTareas: [{
                ...tarea,
                Orden: 1,
                De: auth.user!.IdUsuario,
                Para: workOrder.Para,
            }],
        };
        if (!delayShipment) wo.FechaEnvioOrden = '';
        return wo;
    };

    const onSave = () => {
        if (!auth.user) return;
        setLoading(true);
        ApiService.Post<number>('OrdenesTrabajo', 'Set', auth.user, { ItemOrdenes: buildPayload() })
            .then(() => {
                props.onWorkOrderCreated();
                props.onClose();
                setLoading(false);
            }, (err: string) => { setError(err); setLoading(false); });
    };

    const onSaveAndEdit = () => {
        if (!auth.user) return;
        setLoading(true);
        ApiService.Post<number>('OrdenesTrabajo', 'Set', auth.user, { ItemOrdenes: buildPayload() })
            .then((id: number) => {
                props.onWorkOrderCreated();
                setLoading(false);
                props.onClose();
                if (props.onWorkOrderCreatedAndEdit) props.onWorkOrderCreatedAndEdit(id);
            }, (err: string) => { setError(err); setLoading(false); });
    };

    const operatorOptions: IDropdownOption<ItemOperario>[] = operators.map(o => ({
        key: o.IdUsuario, text: o.Nombre, data: o,
    }));

    const typeOptions: IDropdownOption<ItemTipoIncidencia>[] = incidentTypes.map(it => ({
        key: it.Id, text: it.Descripcion, data: it,
    }));

    /* ── Shared address picker (used in both views) ── */
    const addressCard = (
        <CustomerCardComponent
            nombre={workOrder.Nombre}
            direccion={workOrder.Direccion}
            cpostal={workOrder.CPostal}
            poblacion={workOrder.Poblacion}
            provincia={workOrder.Provincia}
            pais={workOrder.Pais}
            telefono={workOrder.Telefono || ''}
            email={workOrder.Email || ''}
            web={workOrder.Web || ''}
            addressList={addresses}
            onCustomerSelected={() => {}}
            onContactChanged={onAddressChanged} />
    );

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onClose}
            type={PanelType.medium}
            headerText="Nuevo elemento"
            styles={panelStyles}>

            {!showMore ? (
                /* ═══════════════════════════════════════
                   VISTA RÁPIDA
                ═══════════════════════════════════════ */
                <>
                    <div className={styles.fieldRowFirst}>
                        <span className={styles.fieldLabel}>Asunto</span>
                        <div className={styles.fieldValue}>
                            <TextField
                                value={tarea.Descripcion}
                                onChange={(_e, v) => setTarea(t => ({ ...t, Descripcion: v || '' }))} />
                        </div>
                    </div>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Contacto que solicita la tarea</span>
                        <div className={styles.fieldValue}>
                            <CustomerCardComponent
                                displayText={contactDisplayName}
                                nombre={workOrder.Nombre}
                                direccion={workOrder.Direccion}
                                cpostal={workOrder.CPostal}
                                poblacion={workOrder.Poblacion}
                                provincia={workOrder.Provincia}
                                pais={workOrder.Pais}
                                telefono={workOrder.Telefono || ''}
                                email={workOrder.Email || ''}
                                web={workOrder.Web || ''}
                                onCustomerSelected={onSelectCustomer}
                                onContactChanged={onContactChanged} />
                        </div>
                    </div>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Dónde se realiza la tarea</span>
                        <div className={styles.fieldValue}>
                            {addressCard}
                        </div>
                    </div>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Programación</span>
                        <div className={styles.fieldValue}>
                            <DateTimePickerComponent
                                value={tarea.FechaHoraInicioPrevista}
                                onChange={(v) => setTarea(t => ({
                                    ...t,
                                    FechaHoraInicioPrevista: v,
                                    FechaPrevistaFin: addMinutes(v, tiempoPrevisto),
                                }))} />
                        </div>
                    </div>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Asignación</span>
                        <div className={styles.fieldValue}>
                            <Dropdown
                                placeholder="(no hay usuario asignado)"
                                options={operatorOptions}
                                selectedKey={workOrder.Para || null}
                                onChange={onChangeOperator} />
                        </div>
                    </div>
                </>
            ) : (
                /* ═══════════════════════════════════════
                   VISTA COMPLETA (todos los campos)
                ═══════════════════════════════════════ */
                <>
                    {/* IDENTIFICACIÓN */}
                    <span className={styles.sectionHeader}>Identificación</span>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Tipo de elemento</span>
                        <div className={styles.fieldValue}>
                            <Dropdown
                                placeholder="Seleccionar tipo..."
                                options={typeOptions}
                                selectedKey={incidentTypes.find(it => it.Serie === workOrder.Serie)?.Id || null}
                                onChange={onChangeIncidentType} />
                        </div>
                    </div>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Localización que solicita la actuación</span>
                        <div className={styles.fieldValue}>
                            <CustomerCardComponent
                                displayText={contactDisplayName}
                                nombre={workOrder.Nombre}
                                direccion={workOrder.Direccion}
                                cpostal={workOrder.CPostal}
                                poblacion={workOrder.Poblacion}
                                provincia={workOrder.Provincia}
                                pais={workOrder.Pais}
                                telefono={workOrder.Telefono || ''}
                                email={workOrder.Email || ''}
                                web={workOrder.Web || ''}
                                onCustomerSelected={onSelectCustomer}
                                onContactChanged={onContactChanged} />
                        </div>
                    </div>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>
                            Identificador
                            <span className={styles.fieldLabelOptional}> (opcional)</span>
                        </span>
                        <div className={styles.fieldValue}>
                            <TextField
                                value={workOrder.IdExterno}
                                onChange={(_e, v) => setWorkOrder(wo => ({ ...wo, IdExterno: v || '' }))} />
                        </div>
                    </div>

                    {/* PLANIFICACIÓN */}
                    <span className={styles.sectionHeader}>Planificación</span>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Fecha de alta</span>
                        <div className={styles.fieldValue}>
                            <DateTimePickerComponent
                                value={workOrder.FechaAlta}
                                onChange={(v) => setWorkOrder(wo => ({ ...wo, FechaAlta: v }))} />
                        </div>
                    </div>

                    <div className={styles.checkRow}>
                        <Checkbox
                            label="Retrasar el envío"
                            checked={delayShipment}
                            onChange={(_e, checked) => {
                                setDelayShipment(!!checked);
                                if (!checked) setWorkOrder(wo => ({ ...wo, FechaEnvioOrden: '' }));
                            }} />
                    </div>

                    {delayShipment && (
                        <div className={styles.fieldRow}>
                            <span className={styles.fieldLabel}>Fecha de envío</span>
                            <div className={styles.fieldValue}>
                                <DateTimePickerComponent
                                    value={workOrder.FechaEnvioOrden}
                                    onChange={(v) => setWorkOrder(wo => ({ ...wo, FechaEnvioOrden: v }))} />
                            </div>
                        </div>
                    )}

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Fecha y hora prevista de inicio</span>
                        <div className={styles.fieldValue}>
                            <DateTimePickerComponent
                                value={tarea.FechaHoraInicioPrevista}
                                onChange={(v) => setTarea(t => ({
                                    ...t,
                                    FechaHoraInicioPrevista: v,
                                    FechaPrevistaFin: addMinutes(v, tiempoPrevisto),
                                }))} />
                        </div>
                    </div>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>
                            Tiempo previsto (minutos)
                            <span className={styles.fieldLabelOptional}> (opcional)</span>
                        </span>
                        <div className={styles.fieldValue}>
                            <TextField
                                type="number"
                                value={tiempoPrevisto.toString()}
                                onChange={onChangeTiempoPrevisto} />
                        </div>
                    </div>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Asignado a usuario</span>
                        <div className={styles.fieldValue}>
                            <Dropdown
                                placeholder="(no hay usuario asignado)"
                                options={operatorOptions}
                                selectedKey={workOrder.Para || null}
                                onChange={onChangeOperator} />
                        </div>
                    </div>

                    {/* DESCRIPCIÓN DE LA ACTUACIÓN INICIAL */}
                    <span className={styles.sectionHeader}>Descripción de la actuación inicial</span>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Dirección de la actuación</span>
                        <div className={styles.fieldValue}>
                            {addressCard}
                        </div>
                    </div>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>
                            Referencia
                            <span className={styles.fieldLabelOptional}> (opcional)</span>
                        </span>
                        <div className={styles.fieldValue}>
                            <TextField
                                value={tarea.Referencia}
                                onChange={(_e, v) => setTarea(t => ({ ...t, Referencia: v || '' }))} />
                        </div>
                    </div>

                    <div className={styles.checkRow}>
                        <Checkbox
                            label="Habilitar registro de tiempos en la App"
                            checked={tarea.ControlDeTiempos === 1}
                            onChange={(_e, checked) => setTarea(t => ({ ...t, ControlDeTiempos: checked ? 1 : 0 }))} />
                    </div>

                    <div className={styles.checkRow}>
                        <Checkbox
                            label="Enviar Prueba de Entrega a la localización"
                            checked={tarea.PEContacto}
                            disabled
                            onChange={(_e, checked) => setTarea(t => ({ ...t, PEContacto: !!checked }))} />
                    </div>

                    <div className={styles.checkRow}>
                        <Checkbox
                            label="Enviar Prueba de Entrega a la dirección de actuación"
                            checked={tarea.PEDireccion}
                            disabled
                            onChange={(_e, checked) => setTarea(t => ({ ...t, PEDireccion: !!checked }))} />
                    </div>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Asunto</span>
                        <div className={styles.fieldValue}>
                            <TextField
                                value={tarea.Descripcion}
                                onChange={(_e, v) => setTarea(t => ({ ...t, Descripcion: v || '' }))} />
                        </div>
                    </div>

                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>
                            Descripción
                            <span className={styles.fieldLabelOptional}> (opcional)</span>
                        </span>
                        <div className={styles.fieldValue}>
                            <TextField
                                multiline
                                rows={3}
                                value={tarea.Observaciones}
                                onChange={(_e, v) => setTarea(t => ({ ...t, Observaciones: v || '' }))} />
                        </div>
                    </div>
                </>
            )}

            <ErrorMessageComponent message={error} />

            {/* FOOTER */}
            {showMore ? (
                <div className={styles.footerFull}>
                    <DefaultButton
                        iconProps={{ iconName: 'ChevronLeft' }}
                        text="Menos opciones"
                        onClick={() => setShowMore(false)} />
                    <div className={styles.buttonGroup}>
                        <PrimaryButton
                            text="Guardar y editar"
                            className={styles.saveButton}
                            onClick={onSaveAndEdit} />
                        <PrimaryButton
                            text="Guardar"
                            className={styles.saveButton}
                            onClick={onSave} />
                    </div>
                </div>
            ) : (
                <div className={styles.footerQuick}>
                    <DefaultButton
                        text="Más opciones >"
                        onClick={() => setShowMore(true)} />
                    <PrimaryButton
                        text="Guardar"
                        className={styles.saveButton}
                        onClick={onSave} />
                </div>
            )}

            <LoaderComponent loading={loading} />
        </Panel>
    );
};

export default WorkOrderCreateComponent;